import { useState } from "react"
import { Box } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import Figure from "../generic/Figure"
import FigureContent from "../generic/FigureContent"
import AlertsDialog from "../../components/alerts/AlertsDialog"
import { AlertWithSource } from "../../hooks/useAssetAlerts"

interface AlertsPerformanceFigureProps {
  alerts: AlertWithSource[]
  totalCount: number
  assetId?: string
  loading?: boolean
  error?: string
  onAlertStatusUpdate?: (alertId: string, newStatus: string) => void
  refetchQueries?: string[]
  compact?: boolean
  /** System ID to enable linking to system alerts view */
  systemId?: string
  /** Enable link mode (navigate to alerts subsection instead of dialog) */
  linkToAlertsView?: boolean
}

function AlertsPerformanceFigure({
  alerts,
  totalCount,
  assetId,
  loading = false,
  error,
  onAlertStatusUpdate,
  refetchQueries = [],
  compact = false,
  systemId,
  linkToAlertsView = false,
}: AlertsPerformanceFigureProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const hasAlerts = totalCount > 0

  const handleClick = () => {
    if (!hasAlerts) return

    if (systemId && linkToAlertsView) {
      navigate(`/systems/${systemId}/alerts`)
    } else {
      setOpen(!open)
    }
  }

  const trigger = (
    <Box as="span" textDecoration={hasAlerts ? "underline" : "none"} cursor={hasAlerts ? "pointer" : "default"}>
      {hasAlerts ? "active alerts" : "no active alerts"}
    </Box>
  )

  return (
    <Figure heading="Alerts" loading={loading} error={error} compact={compact}>
      <FigureContent.Root compact={compact}>
        <FigureContent.SubText compact={compact}>
          {systemId && linkToAlertsView ? (
            <Box
              as="span"
              textDecoration={hasAlerts ? "underline" : "none"}
              cursor={hasAlerts ? "pointer" : "default"}
              onClick={handleClick}
            >
              {hasAlerts ? "active alerts" : "no active alerts"}
            </Box>
          ) : (
            <AlertsDialog
              alerts={alerts}
              totalCount={totalCount}
              open={open}
              onOpenChange={setOpen}
              assetId={assetId}
              onAlertStatusUpdate={onAlertStatusUpdate}
              refetchQueries={refetchQueries}
              title="Alerts"
              trigger={trigger}
              updateNote="Updated from alerts figure"
            />
          )}
        </FigureContent.SubText>
        <FigureContent.Value onClick={handleClick} compact={compact} cursor={hasAlerts ? "pointer" : "default"}>
          {totalCount} alert{totalCount === 1 ? "" : "s"}
        </FigureContent.Value>
      </FigureContent.Root>
    </Figure>
  )
}

export default AlertsPerformanceFigure
